/**
 * Onboarding Configuration
 *
 * This file defines the onboarding flow for different user roles.
 * It's designed to be flexible - you can add, remove, or reorder steps
 * without breaking the system.
 */

export type OnboardingStep =
  | "signup"
  | "signup/2"
  | "signup/3"
  | "signup/4"
  | "onboarding/goal-setting"
  | "onboarding/advisor-matching"
  | "complete";

export interface OnboardingStepConfig {
  /** The route path for this step */
  route: string;
  /** The step identifier stored in the database */
  step: OnboardingStep;
  /** Whether this step requires authentication */
  requiresAuth: boolean;
  /** Whether this step is protected (part of onboarding flow) */
  isProtected: boolean;
  /** Roles that can access this step (empty array = all roles) */
  allowedRoles: string[];
  /** Next step in the flow (null if this is the last step) */
  nextStep: OnboardingStep | null;
  /**
   * v3: the step is no longer part of any role's signup flow, but the value
   * still exists in `users.onboarding_step` for people who were mid-flow when
   * it was retired. Nothing routes here anymore; such users are treated as
   * having finished onboarding rather than being trapped on a dead step.
   */
  retired?: boolean;
}

/**
 * Onboarding flow configuration
 *
 * This defines the order and requirements for each onboarding step.
 * The order matters - users must complete steps in sequence.
 */
export const ONBOARDING_STEPS: Record<OnboardingStep, OnboardingStepConfig> = {
  signup: {
    route: "/signup",
    step: "signup",
    requiresAuth: false,
    isProtected: false,
    allowedRoles: [],
    nextStep: "signup/2",
  },
  "signup/2": {
    route: "/signup/2",
    step: "signup/2",
    requiresAuth: true,
    isProtected: true,
    allowedRoles: [],
    nextStep: "signup/3",
  },
  "signup/3": {
    route: "/signup/3",
    step: "signup/3",
    requiresAuth: true,
    isProtected: true,
    allowedRoles: [],
    // Achievers finish here (Growth); Experts continue from Operator DNA to
    // the S.T.A.R. Portfolio -- see getNextStep for the achiever short-cut.
    nextStep: "signup/4",
  },
  // Experts only: S.T.A.R. Portfolio, the last signup step.
  "signup/4": {
    route: "/signup/4",
    step: "signup/4",
    requiresAuth: true,
    isProtected: true,
    allowedRoles: ["advisor"],
    nextStep: "complete",
  },
  // v3: retired steps. They stay in the config so values already stored in
  // users.onboarding_step still resolve to something, but no flow routes into
  // them and nothing may write them back.
  "onboarding/goal-setting": {
    route: "/onboarding/goal-setting",
    step: "onboarding/goal-setting",
    requiresAuth: true,
    isProtected: true,
    allowedRoles: ["achiever"],
    nextStep: "complete",
    retired: true,
  },
  "onboarding/advisor-matching": {
    route: "/onboarding/advisor-matching",
    step: "onboarding/advisor-matching",
    requiresAuth: true,
    isProtected: true,
    allowedRoles: ["achiever"],
    nextStep: "complete",
    retired: true,
  },
  complete: {
    route: "/home",
    step: "complete",
    requiresAuth: true,
    isProtected: false,
    allowedRoles: [],
    nextStep: null,
  },
};

/**
 * Get the step configuration for a given route
 */
export function getStepByRoute(route: string): OnboardingStepConfig | null {
  const step = Object.values(ONBOARDING_STEPS).find((config) => config.route === route);
  return step || null;
}

/**
 * Get the step configuration for a given step identifier
 */
export function getStepConfig(step: OnboardingStep | null): OnboardingStepConfig | null {
  if (!step) return null;
  return ONBOARDING_STEPS[step] || null;
}

/**
 * Get the next step in the onboarding flow
 *
 * @param currentStep - Current onboarding step
 * @param userRole - User's role (achiever, advisor, etc.)
 */
export function getNextStep(
  currentStep: OnboardingStep | null,
  userRole?: string,
): OnboardingStep | null {
  if (!currentStep) return "signup";

  const config = getStepConfig(currentStep);
  if (!config) return null;

  // For advisors, skip goal-setting and advisor-matching (they go to complete after signup/4)
  if (userRole === "advisor" && config.nextStep === "onboarding/goal-setting") {
    return "complete";
  }

  // signup/4 is the Expert-only S.T.A.R. step; achievers finish at signup/3.
  const nextConfig = config.nextStep ? getStepConfig(config.nextStep) : null;
  if (nextConfig && !isStepForRole(nextConfig, userRole)) {
    return nextConfig.nextStep;
  }

  return config.nextStep;
}

/**
 * Check if a user has completed onboarding.
 *
 * Complete when the step is explicitly "complete", or when the stored step is
 * one nobody can finish anymore:
 *
 *  - a retired step (v3 dropped goal-setting / advisor-matching from the flow),
 *  - a step this user's role never walks through,
 *  - an unrecognised value left by an older build.
 *
 * Any of those used to read as "not complete", which made OnboardingGuard bounce
 * the user off every route in the app -- in both workspaces, with no failing API
 * call to explain it. A step that cannot be completed must never gate the app.
 *
 * @param userStep - User's current onboarding step
 * @param userRole - User's role, used to test whether the step is theirs to walk
 */
export function isOnboardingComplete(userStep: OnboardingStep | null, userRole?: string): boolean {
  if (userStep === "complete") return true;
  if (!userStep) return false;

  const config = getStepConfig(userStep);
  // Unknown/legacy value: not a reason to lock someone out.
  if (!config) return true;
  if (config.retired) return true;

  return !isStepForRole(config, userRole);
}

/**
 * Whether a step belongs to this role's flow.
 *
 * Reads the step's own `allowedRoles` ([] meaning "every role"), so retargeting
 * a step updates the guard and the redirect logic together instead of leaving
 * them to drift apart.
 */
export function isStepForRole(config: OnboardingStepConfig | null, userRole?: string): boolean {
  if (!config) return false;
  if (config.allowedRoles.length === 0) return true;
  // Role not known yet (e.g. /users/me still in flight) — don't redirect on a guess.
  if (!userRole) return true;
  return config.allowedRoles.includes(userRole);
}

/**
 * Get the previous step in the onboarding flow by finding which step's nextStep points to currentStep.
 *
 * Retired steps are skipped as candidates: several of them now point at
 * "complete", so without this filter a live step could resolve its "previous"
 * step to a dead one and offer a Back button into a route no flow uses.
 */
export function getPreviousStep(currentStep: OnboardingStep | null): OnboardingStep | null {
  if (!currentStep) return null;
  const entry = Object.values(ONBOARDING_STEPS).find(
    (config) => config.nextStep === currentStep && !config.retired,
  );
  return entry?.step || null;
}

/**
 * Get the route that a user should be redirected to based on their onboarding progress
 *
 * This is the PRIMARY function used after login to determine where to send the user.
 *
 * @param userStep - Current onboarding step from database
 * @param userRole - User's role (achiever, advisor, etc.)
 */
export function getRedirectRoute(userStep: OnboardingStep | null, userRole?: string): string {
  // Complete, retired, wrong-role, or unrecognised step -> the app proper.
  if (isOnboardingComplete(userStep, userRole)) {
    return "/home";
  }

  // If no step, user needs to complete profile setup (after email verification)
  if (!userStep) {
    return "/login";
  }

  // Return the route for the user's current step
  const config = getStepConfig(userStep);
  return config?.route || "/login";
}

/**
 * Get the ordered list of steps for a user based on their role
 *
 * @param userRole - User's role
 */
export function getStepsForRole(userRole?: string): OnboardingStep[] {
  const allSteps: OnboardingStep[] = [
    "signup",
    "signup/2",
    "signup/3",
    "signup/4",
    "onboarding/goal-setting",
    "onboarding/advisor-matching",
    "complete",
  ];

  // Advisors skip goal-setting and advisor-matching
  if (userRole === "advisor") {
    return allSteps.filter(
      (step) => step !== "onboarding/goal-setting" && step !== "onboarding/advisor-matching",
    );
  }

  // signup/4 (S.T.A.R. Portfolio) is Experts only.
  return allSteps.filter((step) => step !== "signup/4");
}
